/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Switch,
  Divider,
  TableRow,
  TableCell,
  Typography,
  CircularProgress
} from '@mui/material';
import _ from 'lodash';
import { formatDate, fromEpochSeconds } from '../../../../../../utils/formatDate';
import { ClockIcon, AnswerIcon, PreviewIcon } from '../../../../../../utils/Icons';
import {
  roleNameFormatter,
  unixToHumanReadable,
  formatNumberWithCommas,
  GroupBasedRoleFormatter
} from '../../../../components/common';
import { toast } from 'react-toastify';
import { API } from '../../../../../../api';
import BlockIcon from '@mui/icons-material/Block';
import Upvotes from '../../../../components/Upvotes';
import ProgramDeletDialog from '../ProgramDeletDialog';
import DosenButton from '../../../../../../components/ui/Buttons/DosenButton';
import MenteeProfileModal from '../../../home/MentorDash/Components/MenteeProfileModal';
import { useNavigate, useParams } from 'react-router-dom';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import ViewObjectivesDialog from '../ViewObjectivesDialog';
import RoleTypeTag from '../../../../components/RoleTypeTag';
import { MemberRole, isMemberRole, useMemberWord } from '../../../../../../utils/memberTypeLabels';
import { useModule } from '../../../../../../utils/useModule';
import { getUserDetails } from '../../../../../../utils/orgName';
import ChatAvatarComponent from '../../../chat/ChatAvatarComponent';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import LinearProgressWithLabel from '../../../Matches/components/LinearProgressWithLabel';
import { acsTokenActions } from '../../../../../../redux/slices/acs-token/acsTokenSlice';
import { acsChannelActions } from '../../../../../../redux/slices/acs-channels/acsChannels';
import { IGroupUser, groupUsersActions } from '../../../../../../redux/slices/group-users/groupUsersSlice';
import { communityUsersActions } from '../../../../../../redux/slices/acs-community-members/acsCommunityMembers';

interface Props {
  data: IGroupUser;
  closedUserId: any;
  setShowProfile: any;
  setActiveUserId: any;
  scrollToUserCall: any;
  setScrollToUserCall: any;
  showGoalProgress: boolean;
  handleOpenTasks: any;
  showCompany?: boolean;
}

const ProgramUserTableRow = ({
  data,
  setActiveUserId,
  setShowProfile,
  scrollToUserCall,
  showGoalProgress,
  setScrollToUserCall,
  closedUserId,
  handleOpenTasks,
  showCompany
}: Props) => {
  const { groupId: grpId, orgId: ctxOrgId } = useProgramContext();
  const { isMasterclass } = useModule();
  const { isMentorship } = useModule();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const usersLoginDetails = useAppSelector((state) => state.groupUsers.data?.users);
  const w = useMemberWord();

  const companyName = data.company?.companyName || '-';
  const companyLocation = data.company?.companyLocation || '-';
  const roleList = Array.isArray(data.role)
    ? data.role
    : data.role
    ? [data.role]
    : (data as any).memberRole
    ? [(data as any).memberRole]
    : (data as any).roleName
    ? [(data as any).roleName]
    : [];

  const [openDelete, setDelete] = useState(false);
  const [openActive, setActiveDialog] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [objectivesData, setObjectivesData] = useState<any[]>([]);
  const [defaultMatch, setDefaultMatch] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);

  // View Profile modal (same profile shown from Matches → Suggested Matches → profile icon)
  const [openProfile, setOpenProfile] = useState(false);
  const [profileUser, setProfileUser] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const activeItemRef = useRef<any>(null);

  /** SCROLL TO USER */
  const scrollToUser = () => {
    if (activeItemRef.current) {
      activeItemRef?.current?.scrollIntoView({ block: 'center' });
    }
  };

  useEffect(() => {
    if (scrollToUserCall) {
      scrollToUser();
      setScrollToUserCall(false);
    }
  }, [scrollToUserCall === true]);

  useEffect(() => {
    setDefaultMatch(data.defaultMatch?.includes(grpId) ?? false);
  }, [data]);

  /** VIEW PROFILE */
  const handleViewProfile = async () => {
    setProfileLoading(true);
    try {
      const response = await API.getUserProfileInAGroup(grpId, data.userId);
      if (response.status === 200) {
        setProfileUser(response.data?.userDetails || response.data?.data || {});
        setOpenProfile(true);
      } else {
        toast.error('This profile could not be loaded. Refresh the page and try again.');
      }
    } catch {
      toast.error('This profile could not be loaded. Refresh the page and try again.');
    }
    setProfileLoading(false);
  };

  /** REMOVE MEMBER */
  const removeUser = async () => {
    setDeleteLoader(true);
    try {
      const response = await API.removeUserFromGroup({
        grpId,
        userId: data.userId
      });

      if (response.status === 200) {
        toast.success('This person was removed from the program.');
        dispatch(groupUsersActions.removeUser({ userId: data.userId }));
        setDelete(false);
      }
    } catch {
      toast.error('This person could not be removed. Try again.');
    }
    setDeleteLoader(false);
  };

  /** ACTIVATE / DEACTIVATE */
  const activateUser = async () => {
    setDeleteLoader(true);
    try {
      const response = await API.activateUser({
        isActive: !data.isActive,
        grpId,
        userId: data.userId
      });

      if (response.status === 200) {
        dispatch(
          groupUsersActions.updateUser({
            ...data,
            isActive: !data.isActive
          })
        );
        toast.success(`This person was ${data.isActive ? 'deactivated' : 'activated'}.`);
        setActiveDialog(false);
      }
    } catch {
      toast.error('This person could not be updated. Try again.');
    }
    setDeleteLoader(false);
  };

  /** MATCH BY DEFAULT */
  const handleMatchByDefault = async (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSwitchLoading(true);

    try {
      const response = await API.defaultMatchRequest({
        grpId,
        mentorId: data.userId,
        value: checked
      });

      if (response.status === 200) {
        let defaultMatch = [...(data.defaultMatch || [])];
        checked ? defaultMatch.push(grpId) : (defaultMatch = defaultMatch.filter((x) => x !== grpId));

        dispatch(groupUsersActions.updateUser({ ...data, defaultMatch }));
      }
    } catch {
      toast.error('The default match could not be saved. Try again.');
    }

    setSwitchLoading(false);
  };

  /** LEARNING OBJECTIVES */
  const orgId = ctxOrgId;
  const fetchObjectives = async () => {
    try {
      const response = await API.adminViewObjectives({
        orgId,
        userId: data.userId,
        grpId
      });

      setObjectivesData(response.data.data);
    } catch {
      toast.error('Learning objectives could not be loaded. Refresh the page and try again.');
      setObjectivesData([]);
    }
  };

  const handleOpenObjectives = () => {
    fetchObjectives();
    setObjectivesData([]);
  };

  const COLS = {
    checkbox: 22,
    name: 200,
    role: 150,
    lastLogin: 150,
    programs: 80,
    onboarding: 150,
    onboardedDate: 150,
    goalProgress: 140,
    experience: 150,
    meetings: 100,
    actions: 220
  };

  return (
    <>
      <TableRow
        sx={{
          height: 65,
          borderBottom: '1px solid #2A2A2A',
          bgcolor: '#151515',
          '&:hover': { bgcolor: '#151515' }
        }}
      >
        {/* 1 — checkbox */}
        <TableCell sx={{ width: COLS.checkbox }} />

        {/* 2 — NAME */}
        <TableCell sx={{ width: COLS.name }}>
          <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>{data.name?.trim() || `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim() || data.email}</Typography>
          </Box>
        </TableCell>

        {/* 3 — ROLE */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.role }}>
          <Box display="flex" gap={1}>
            {roleList.map((r) => (
              <RoleTypeTag
                key={r}
                category={_.startCase(r)}
                label={isMemberRole(r) ? w(r.trim().toLowerCase() as MemberRole) : undefined}
                place="userTable"
              />
            ))}
          </Box>
        </TableCell>

        {/* 4 — LAST LOGIN */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.lastLogin }}>{unixToHumanReadable(data.lastLogin)}</TableCell>

        {/* 5 — PROGRAMS */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.programs, textAlign: 'center' }}>{data.sentMessages}</TableCell>

        {/* 6 — ONBOARDING STATUS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.onboarding }}>
          {_.upperFirst(_.lowerCase(data.onboardStatus))}
        </TableCell>

        {/* 7 — ONBOARDED DATE */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.onboardedDate }}>
          {data.onboardedDate ? formatDate(fromEpochSeconds(data.onboardedDate)) : ''}
        </TableCell>

        {/* 8 — GOAL PROGRESS */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.goalProgress, textAlign: 'center' }}>
          {roleList.includes('mentee') ? <LinearProgressWithLabel value={data.goalProgress ?? 0} /> : 'N/A'}
        </TableCell>

        {/* 9 — EXPERIENCE POINTS */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.experience, textAlign: 'center' }}>
          {data.xpPoints ?? 0}
        </TableCell>

        {/* 10 — MEETINGS */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.meetings, textAlign: 'center' }}>{data.meetings}</TableCell>

        {/* 11 — ACTIONS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.actions }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
            <DosenButton
              tone="basic"
              sizeVariant="xs"
              loading={profileLoading}
              loadingText="Loading..."
              onClick={handleViewProfile}
              sx={{ textAlign: 'center' }}
            >
              View Profile
            </DosenButton>
            <DosenButton
              tone="basic"
              sizeVariant="xs"
              onClick={() => setDelete(true)}
              sx={{
                textAlign: 'center',
                color: 'error.main',
                borderColor: 'rgba(244, 67, 54, 0.4)'
              }}
            >
              Remove
            </DosenButton>
          </Box>
        </TableCell>
      </TableRow>

      {/* View Profile modal — same component as Matches → Suggested Matches */}
      <MenteeProfileModal
        open={openProfile}
        onClose={() => {
          setOpenProfile(false);
          setProfileUser(null);
        }}
        user={profileUser || {}}
      />

      {/* DELETE DIALOG */}
      <ProgramDeletDialog
        openDelete={openDelete}
        setDelete={setDelete}
        deleteLoader={deleteLoader}
        text="Are you sure you want to remove this member?"
        handleDelete={removeUser}
        loadingText="Removing..."
      />

      {/* ACTIVATE / DEACTIVATE DIALOG */}
      <ProgramDeletDialog
        openDelete={openActive}
        setDelete={setActiveDialog}
        deleteLoader={deleteLoader}
        text={`Are you sure you want to ${data.isActive ? 'deactivate' : 'reactivate'} this member?`}
        handleDelete={activateUser}
        btnText={data.isActive ? 'Deactivate' : 'Reactivate'}
        loadingText={data.isActive ? 'Deactivating...' : 'Reactivating...'}
      />
    </>
  );
};

export default ProgramUserTableRow;
